
import { useState } from 'react';

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  category: string;
  location: string;
  flag: string;
  year: string;
  duration: string;
  heroImage: string;
  thumbnail: string;
  challenge: string;
  solution: string;
  results: { metric: string; label: string }[];
  services: string[];
  testimonial: { quote: string; author: string; role: string };
  gallery: string[];
}

interface CaseStudyGridProps {
  projects: CaseStudy[];
  onSelectProject: (project: CaseStudy) => void;
}

export default function CaseStudyGrid({ projects, onSelectProject }: CaseStudyGridProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (projects.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <i className="ri-folder-open-line text-2xl text-gray-300"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-400 mb-1">No projects found</h3>
        <p className="text-sm text-gray-400">Try selecting a different category.</p>
      </div>
    );
  }

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <article
              key={project.id}
              className={`group relative bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#038C7F]/20 hover:shadow-2xl hover:shadow-[#038C7F]/5 transition-all duration-500 cursor-pointer ${
                index === 0 ? 'md:col-span-2' : ''
              }`}
              onClick={() => onSelectProject(project)}
              onMouseEnter={() => setHoveredId(project.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className={`relative overflow-hidden ${index === 0 ? 'h-[300px] sm:h-[400px] lg:h-[480px]' : 'h-[240px] sm:h-[300px]'}`}>
                <img
                  src={project.heroImage}
                  alt={project.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/90 via-[#0D0D0D]/30 to-transparent"></div>

                {/* Category badge */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-[#0D0D0D] text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wider">
                    {project.category}
                  </span>
                  <span className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-[10px] sm:text-xs font-medium rounded-full">
                    {project.year}
                  </span>
                </div>

                {/* Arrow indicator */}
                <div className={`absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 ${hoveredId === project.id ? 'bg-[#038C7F] scale-110' : ''}`}>
                  <i className={`ri-arrow-right-up-line text-lg text-white transition-transform duration-300 ${hoveredId === project.id ? 'rotate-45' : ''}`}></i>
                </div>

                {/* Bottom content overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-base">{project.flag}</span>
                    <span className="text-xs font-medium text-white/70">{project.client}</span>
                    <span className="w-1 h-1 bg-white/30 rounded-full"></span>
                    <span className="text-xs text-white/50">{project.location}</span>
                  </div>
                  <h3 className={`font-bold text-white leading-tight ${index === 0 ? 'text-xl sm:text-2xl lg:text-3xl' : 'text-lg sm:text-xl'}`}>
                    {project.title}
                  </h3>
                </div>
              </div>

              {/* Bottom info bar */}
              <div className="p-4 sm:p-5 flex items-center justify-between">
                <div className="flex items-center gap-3 overflow-hidden">
                  {project.services.slice(0, 3).map((service, idx) => (
                    <span
                      key={idx}
                      className="px-2.5 py-1 bg-gray-50 text-[10px] sm:text-xs font-medium text-gray-500 rounded-md whitespace-nowrap"
                    >
                      {service}
                    </span>
                  ))}
                  {project.services.length > 3 && (
                    <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">+{project.services.length - 3} more</span>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-xs text-gray-400"></i>
                    <span className="text-[10px] sm:text-xs text-gray-400 font-medium">{project.duration}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[#038C7F] font-semibold text-xs group-hover:gap-2.5 transition-all">
                    <span className="hidden sm:inline">View Case Study</span>
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
